import { Injectable, OnDestroy } from '@angular/core'
import { CognitoUser, CognitoUserPool, CognitoUserSession } from 'amazon-cognito-identity-js'
import { map, bindNodeCallback, Observable, catchError, of } from 'rxjs'
import { AppConfigService } from './app-config.service'

type CognitoRequestError = { code?: string, nonRetryable?: boolean }
type CognitoRequestCallback = (error: CognitoRequestError | null, result?: unknown) => void
type CognitoRequestClient = {
  request: (operation: string, params: unknown, callback: CognitoRequestCallback) => void
}

// The library retries every requestWithRetry error, NotAuthorizedException included.
// The loop stops when error.nonRetryable is set. Leave the pool unchanged if its private client is missing.
const markRefusedRefreshAsFinal = (userPool: CognitoUserPool): CognitoUserPool => {
  const client = (userPool as unknown as { client?: CognitoRequestClient }).client
  if (typeof client?.request !== 'function') return userPool
  const request = client.request.bind(client)
  client.request = (operation, params, callback) =>
    request(operation, params, (error, result) => {
      if (error?.code === 'NotAuthorizedException') error.nonRetryable = true
      callback(error, result)
    })
  return userPool
}

const getUserSession
  = bindNodeCallback<[user:CognitoUser], [session: CognitoUserSession|null]>((user, cb)=>user.getSession(cb))
@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {

  private userPool: CognitoUserPool|null = null
  private currentUser: CognitoUser|null = null

  private subscriptions = [
    this.appConfig.state$.subscribe((appConfig)=>{
      const authConfig = appConfig.auth2
      this.userPool = markRefusedRefreshAsFinal(new CognitoUserPool({ UserPoolId: authConfig.cognitoUserPoolId, ClientId: authConfig.cognitoClientId }))
      this.currentUser = this.userPool.getCurrentUser()
    })
  ]

  ngOnDestroy (): void {
    this.subscriptions.forEach((subscription)=>subscription.unsubscribe())
  }

  constructor (
    private appConfig: AppConfigService
  ) {}
  private getCurrentUser (): CognitoUser {
    if (this.currentUser === null){
      throw Error('user must be logged in to perform this action')
    }
    return this.currentUser
  }
  getUsername (): string {
    return this.getCurrentUser().getUsername()
  }
  private getUserSession (): Observable<CognitoUserSession> {
    return getUserSession(this.getCurrentUser()).pipe(
      map((session)=>{
        if (session === null){
          throw Error('user session not found')
        }
        return session
      })
    )
  }
  isAuthenticated (): Observable<boolean>{
    return (this.currentUser === null)
      ? of(false)
      : this.getUserSession().pipe(
        map((session)=>session.isValid()),
        catchError(()=>of(false))
      )
  }
  getAccessToken (): Observable<string>{
    return this.getUserSession().pipe(
      map((session)=>session.getAccessToken().getJwtToken())
    )
  }
  logout (): void {
    this.getCurrentUser().signOut()
  }
}

